import { ref, computed } from 'vue';
import QRCode from 'qrcode';
import dinastiaWhatsappChannel from 'dashboard/api/channel/dinastiaWhatsappChannel';

/**
 * Shared composable for Dinastia WhatsApp QR code and connection management.
 * Used by DinastiaConnectionManager and FinishSetup to avoid duplicating
 * QR processing, status polling, and session fetch logic.
 */
export function useDinastiaQr() {
  const qrImage = ref('');
  const connectionStatus = ref('');
  const phoneNumber = ref('');
  const isLoading = ref(false);
  const isActionLoading = ref(false);
  let pollTimer = null;

  const isConnected = computed(() => connectionStatus.value === 'connected');
  const isDisconnected = computed(
    () => connectionStatus.value === 'disconnected'
  );

  const showLoading = computed(() => {
    if (connectionStatus.value === 'connected') return false;
    if (connectionStatus.value === 'disconnected') return false;
    if (qrImage.value) return false;
    return true;
  });

  const showQr = computed(
    () => !!qrImage.value && connectionStatus.value !== 'connected'
  );

  const showConnected = computed(() => connectionStatus.value === 'connected');

  const showDisconnected = computed(
    () => connectionStatus.value === 'disconnected'
  );

  async function processQrData(qr) {
    if (!qr) return;
    if (qr.startsWith('data:image/')) {
      qrImage.value = qr;
    } else {
      qrImage.value = await QRCode.toDataURL(qr, { width: 320, margin: 2 });
    }
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  async function fetchSession(inboxId) {
    if (!inboxId) return;

    isLoading.value = true;
    try {
      const { data } = await dinastiaWhatsappChannel.getSessionStatus(inboxId);
      connectionStatus.value = data.connection_status;
      phoneNumber.value = data.phone_number || '';

      if (data.session_data?.qr) {
        await processQrData(data.session_data.qr);
      } else if (data.connection_status === 'connected') {
        qrImage.value = '';
      }

      if (
        data.connection_status === 'connected' ||
        data.connection_status === 'disconnected'
      ) {
        stopPolling();
      }
    } catch {
      if (!connectionStatus.value) {
        connectionStatus.value = 'disconnected';
      }
    } finally {
      isLoading.value = false;
    }
  }

  async function startPolling(inboxId) {
    if (pollTimer) return;
    await fetchSession(inboxId);
    const status = connectionStatus.value;
    if (status === 'connected' || status === 'disconnected') return;
    pollTimer = setInterval(() => fetchSession(inboxId), 3000);
  }

  async function connect(inboxId) {
    isActionLoading.value = true;
    qrImage.value = '';
    try {
      await dinastiaWhatsappChannel.connect(inboxId);
      connectionStatus.value = 'connecting';
      startPolling(inboxId);
    } catch {
      connectionStatus.value = 'disconnected';
    } finally {
      isActionLoading.value = false;
    }
  }

  async function disconnect(inboxId) {
    isActionLoading.value = true;
    try {
      await dinastiaWhatsappChannel.disconnect(inboxId);
      connectionStatus.value = 'disconnected';
      qrImage.value = '';
      stopPolling();
    } catch {
      // keep current status
    } finally {
      isActionLoading.value = false;
    }
  }

  function cleanup() {
    stopPolling();
  }

  return {
    qrImage,
    connectionStatus,
    phoneNumber,
    isLoading,
    isActionLoading,
    isConnected,
    isDisconnected,
    showLoading,
    showQr,
    showConnected,
    showDisconnected,
    processQrData,
    stopPolling,
    fetchSession,
    startPolling,
    connect,
    disconnect,
    cleanup,
  };
}
